import Link from 'next/link'
import Image from 'next/image'
import { FaClock, FaMapMarkerAlt, FaArrowRight } from 'react-icons/fa'

const FALLBACK_IMAGE = 'https://placehold.co/600x400/333333/ffffff?text=Evento+Vibe'

interface EventCardProps {
  id?: string
  title?: string
  date?: string
  time?: string
  location?: string
  description?: string
  imageUrl?: string
  highlighted?: boolean
  category?: string
}

export default function EventCard({
  id = '1',
  title = 'Nome do Evento',
  date = 'Data do Evento',
  time = 'Horário',
  location = 'Local',
  description = 'Descrição do evento.',
  imageUrl,
  highlighted = false,
  category,
}: EventCardProps) {
  const displayImage = imageUrl || FALLBACK_IMAGE
  
  function formatDate(dateString?: string) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  }

  return (
    <Link 
      href={`/events/${id}`} 
      className={`group block h-full ${highlighted ? 'md:col-span-2 lg:col-span-2' : ''}`}
    >
      <div className={`
        h-full flex flex-col
        rounded-3xl overflow-hidden 
        bg-zinc-900/40 backdrop-blur-sm 
        border border-zinc-800/50
        transition-all duration-500 
        hover:bg-zinc-800/60 hover:border-[#ffa300]/50 hover:shadow-[0_0_30px_rgba(255,163,0,0.15)] hover:-translate-y-2
        ${highlighted ? 'border-[#ffa300]/30 shadow-[0_0_30px_rgba(255,163,0,0.1)]' : ''}
      `}>
        {/* Image Container - aspect-video mobile; 4/3 em desktop para cards maiores */}
        <div className="relative overflow-hidden aspect-video md:aspect-[4/3]">
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent opacity-60 z-10"></div>
          <Image 
            src={displayImage} 
            alt={title}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            className="object-cover transition-transform duration-700 group-hover:scale-110"
            unoptimized={displayImage.startsWith('/uploads/')}
          />
          
          {/* Badges */}
          <div className="absolute top-4 right-4 z-20">
            <div className="bg-black/70 backdrop-blur-md text-[#ffa300] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide border border-white/10 shadow-lg uppercase">
              {formatDate(date)}
            </div>
          </div>

          {category && (
            <div className="absolute top-4 left-4 z-20">
              <div className="bg-[#ffa300] text-black px-4 py-1.5 rounded-full text-xs font-bold tracking-wide shadow-lg uppercase">
                {category}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 md:p-8 flex flex-col flex-1 relative">
          {/* Title */}
          <h3 className="text-2xl md:text-3xl font-black mb-3 text-white group-hover:text-[#ffa300] transition-colors leading-tight">
            {title}
          </h3>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-4 text-zinc-400 text-sm mb-4 font-medium">
            <div className="flex items-center gap-2">
              <FaClock className="text-[#ffa300]" /> 
              {time}
            </div>
            <div className="flex items-center gap-2">
              <FaMapMarkerAlt className="text-[#ffa300]" /> 
              {location}
            </div>
          </div>

          {/* Description */}
          <p className="text-zinc-300 text-base mb-8 line-clamp-2 font-light leading-relaxed flex-1">
            {description}
          </p>

          {/* Action */}
          <div className="flex items-center min-h-[44px] text-[#ffa300] font-bold uppercase tracking-widest text-sm group/btn">
            Ver Detalhes
            <FaArrowRight className="ml-2 transition-transform duration-300 group-hover/btn:translate-x-2" />
          </div>
        </div>
      </div>
    </Link>
  )
}
